import { useMemo, useState } from "react";
import {
  blockRemoteImages,
  hasBlockedImages,
  loadImages,
  sanitizeEmailHtml,
} from "../lib/sanitize";
import type { Theme } from "../lib/theme";

interface EmailBodyProps {
  html: string;
  theme: Theme;
}

const THEME_COLORS: Record<Theme, { background: string; color: string; link: string }> = {
  light: { background: "#faf2ff", color: "#111827", link: "#61506a" },
  dark: { background: "#18151a", color: "#fdf9ff", link: "#f6e5fe" },
};

/**
 * Renderiza HTML de correo (no confiable, CLAUDE.md §3.4) en un
 * <iframe sandbox> sin allow-same-origin + allow-scripts juntos — así ni un
 * bypass de sanitización llega a tocar el DOM real de la app. `allow-popups`
 * es lo único habilitado, para que los links del correo abran en pestaña
 * nueva en vez de quedar completamente inertes.
 */
export default function EmailBody({ html, theme }: EmailBodyProps) {
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const blockedHtml = useMemo(() => blockRemoteImages(sanitizeEmailHtml(html)), [html]);
  const displayHtml = useMemo(
    () => (imagesLoaded ? loadImages(blockedHtml) : blockedHtml),
    [blockedHtml, imagesLoaded],
  );
  const showLoadImagesButton = !imagesLoaded && hasBlockedImages(blockedHtml);

  const srcDoc = useMemo(() => {
    const { background, color, link } = THEME_COLORS[theme];
    // Fondo/texto por defecto siguen el tema de la app (contraste en modo
    // oscuro en vez de forzar una caja blanca) — pero SIN !important: son
    // reglas a nivel de elemento (html/body), así que cualquier correo que
    // traiga su propio color/fondo vía `style` inline (la única forma de
    // estilo que sanitize.ts deja pasar, ver FORBID_TAGS:["style",...]) tiene
    // mayor especificidad y gana igual. Solo correos "en blanco" sin
    // estilo propio heredan estos colores.
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<base target="_blank">
<meta name="color-scheme" content="${theme}">
<style>
  html { color-scheme: ${theme}; background: ${background}; }
  body { font-family: Inter, sans-serif; font-size: 14px; line-height: 1.6; color: ${color}; background: ${background}; margin: 0; padding: 16px; word-wrap: break-word; }
  img { max-width: 100%; height: auto; }
  a { color: ${link}; }
</style>
</head>
<body>${displayHtml}</body>
</html>`;
  }, [displayHtml, theme]);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
      {showLoadImagesButton && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 8,
            background: "var(--color-selected-bg)",
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          <span>Se bloquearon imágenes remotas de este correo.</span>
          <button
            onClick={() => setImagesLoaded(true)}
            style={{
              border: "none",
              background: "var(--color-primary)",
              color: "#fff",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cargar imágenes
          </button>
        </div>
      )}
      <iframe
        title="Contenido del correo"
        sandbox="allow-popups"
        srcDoc={srcDoc}
        style={{ width: "100%", flex: "1 1 auto", minHeight: 200, border: "none" }}
        // Llena el espacio disponible del panel de lectura (responsivo al
        // resize de la ventana/columna) — el iframe scrollea su propio
        // contenido internamente si el correo es más largo que el alto
        // disponible. No puede reportar su altura real de contenido sin
        // allow-same-origin, así que "llenar el contenedor + scroll propio"
        // es la opción segura en vez de intentar medir y ajustar altura.
      />
    </div>
  );
}
